import { AnalyticsEventName, reportUsage } from '@ms/yammer-data/dist/telemetry';
import {
  useIsFoundationalPillarsNavPhase1Enabled,
  useIsHeadersNavPhase1Enabled,
  useIsSearchNavPhase1Enabled,
} from '@ms/yammer-features-app-navigation';
import { useLoadCurrentUserNetworks } from '@ms/yammer-hooks/dist/currentUserNetworks';
import VivaEngageAppInbox from '@ms/yammer-web-components/dist/inbox/VivaEngageAppInbox';
import VivaEngageAppNotifications from '@ms/yammer-web-components/dist/notifications/VivaEngageAppNotifications';
import VivaEngageNavigationHeaderMoreActions from '@ms/yammer-web-components/dist/teams/VivaEngageNavigationHeaderMoreActions';
import VivaEngageNavigationHeaderPivot from '@ms/yammer-web-components/dist/teams/VivaEngageNavigationHeaderPivot';
import {
  SidebarPanelButton,
  useIsContentSidebarCurrentlyHiddenByNarrowWidth,
} from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import Block from '@ms/yammer-web-core-ui/dist/components/Block';
import Flex, { FlexItem } from '@ms/yammer-web-core-ui/dist/components/Flex';
import InternalLink from '@ms/yammer-web-core-ui/dist/components/InternalLink';
import { LayoutList, LayoutListItem } from '@ms/yammer-web-core-ui/dist/components/LayoutList';
import { useIsPageLayoutVNext } from '@ms/yammer-web-core-ui/dist/components/PageLayout';
import { getRTL } from '@ms/yammer-web-core-ui/dist/util/styling/rtl';
import { feedPageUrl } from '@ms/yammer-web-support/dist/urls';
import { useUIFeatures } from '@ms/yammer-web-ui-features/dist/features';
import { useIsTeamsPersonalApp } from 'hooks/selectors';
import React, { FC, SyntheticEvent } from 'react';
import { useHistory } from 'react-router-dom';

import AppHeaderLogo from '../AppHeaderLogo';
import { usePageContext } from '../PageContext';
import Toast from '../Toast';
import VivaEngageAppInsights from '../VivaEngageAppInsights';
import VivaEngageAppSearch from '../VivaEngageAppSearch';
import VivaEngagePanelButton from '../VivaEngagePanelButton';
import VivaSuiteNav from '../VivaSuiteNav';

import { getClassNames } from './VivaEngageAppHeader.styles';
import { useGetNavigationHeaderPivotItems } from './hooks';

const toastTargetId = 'vivaEngageAppHeader--toastTarget';

const VivaEngageAppHeader: FC = () => {
  useLoadCurrentUserNetworks();

  const features = useUIFeatures(({ navigation }) => navigation ?? {});

  const isAppRTL = getRTL();
  const classNames = getClassNames(isAppRTL);
  const pivotItems = useGetNavigationHeaderPivotItems();
  const { location } = useHistory();
  const { shouldDisplayContentPanelButton } = usePageContext();
  const isFoundationalPillarsNavPhase1Enabled = useIsFoundationalPillarsNavPhase1Enabled();
  const isSearchNavPhase1Enabled = useIsSearchNavPhase1Enabled();
  const isHeadersNavPhase1Enabled = useIsHeadersNavPhase1Enabled();
  const isTeamsPersonalApp = useIsTeamsPersonalApp();

  const isPageLayoutVNext = useIsPageLayoutVNext();
  const shouldDisplayAppLayoutSidebarPanelButton =
    useIsContentSidebarCurrentlyHiddenByNarrowWidth() && isPageLayoutVNext;

  const legacyPageLayoutSidebarPanelButton = shouldDisplayContentPanelButton ? <VivaEngagePanelButton /> : null;
  const vNextPageLayoutSidebarPanelButton = shouldDisplayAppLayoutSidebarPanelButton ? <SidebarPanelButton /> : null;
  const sidebarPanelButton = vNextPageLayoutSidebarPanelButton || legacyPageLayoutSidebarPanelButton;
  const sidebarPanelButtonItem = sidebarPanelButton ? <LayoutListItem>{sidebarPanelButton}</LayoutListItem> : null;

  const searchItem =
    features.search && !isSearchNavPhase1Enabled ? (
      <FlexItem>
        <VivaEngageAppSearch />
      </FlexItem>
    ) : null;

  const notificationsItem =
    features.notifications && !isHeadersNavPhase1Enabled ? (
      <FlexItem className={classNames.notificationsItem}>
        <VivaEngageAppNotifications />
      </FlexItem>
    ) : null;

  const inboxItem =
    features.inbox && !isFoundationalPillarsNavPhase1Enabled ? (
      <FlexItem className={classNames.inboxItem}>
        <VivaEngageAppInbox />
      </FlexItem>
    ) : null;

  const insightsItem =
    features.insights && !isSearchNavPhase1Enabled ? (
      <FlexItem>
        <VivaEngageAppInsights />
      </FlexItem>
    ) : null;

  const onAppHeaderLogoClick = (event: SyntheticEvent) => {
    if (location.pathname === feedPageUrl) {
      event.preventDefault();
    }
    reportUsage({ eventName: AnalyticsEventName.VivaEngageIconClicked });
  };

  const suiteNav =
    !isFoundationalPillarsNavPhase1Enabled && isTeamsPersonalApp ? (
      <Flex shrink={0} paddingLeft={4.2} paddingBottom={1} alignItems="center">
        <VivaSuiteNav />
      </Flex>
    ) : null;

  const appBrand = !isFoundationalPillarsNavPhase1Enabled ? (
    <Flex shrink={0} paddingLeft={1.75} alignItems="center">
      <InternalLink onClick={onAppHeaderLogoClick} to={feedPageUrl} className={classNames.internalLink} unstyled={true}>
        <AppHeaderLogo />
      </InternalLink>
    </Flex>
  ) : null;

  const appHeaderContent = (
    <Flex className={classNames.contentWrapper}>
      {suiteNav}
      {appBrand}
      <VivaEngageNavigationHeaderPivot items={pivotItems} linkSize="normal" linkFormat="links" />
      <Flex justifyContent="flex-end" alignItems="center" grow={1} shrink={0}>
        <LayoutList direction="horizontal" isPresentational={true} preventItemWrap={true} showSeparator={true}>
          {sidebarPanelButtonItem}
          <LayoutListItem fillWidth={true}>
            <Flex columnGap={1.5}>
              {searchItem}
              {notificationsItem}
              {inboxItem}
              {insightsItem}
              <FlexItem className={classNames.moreActionsItem}>
                <span id={toastTargetId} />
                <VivaEngageNavigationHeaderMoreActions />
              </FlexItem>
            </Flex>
          </LayoutListItem>
        </LayoutList>
        <Toast targetElementId={toastTargetId} />
      </Flex>
    </Flex>
  );

  return (
    <Block className={classNames.container} paddingTop={1} paddingRight={6} textAlign="right">
      {appHeaderContent}
    </Block>
  );
};

export default VivaEngageAppHeader;
